import { useCallback, useSyncExternalStore } from "react";
import {
  readSiteAppearance,
  writeSiteAppearance,
  SITE_APPEARANCE_KEY,
  type AppearanceMode,
} from "@heyday/news-api/appearance";
import {
  appAppearance,
  appTheme,
  insideHeyDayNewsApp,
  onAppCommand,
  reportSurfaceToApp,
} from "../appMode";
import { applyThemePalette } from "../themePalette";

const MEDIA_QUERY = "(prefers-color-scheme: dark)";
const THEME_COLOR_META_NAME = "theme-color";
const DYNAMIC_THEME_COLOR_SELECTOR = 'meta[name="theme-color"][data-dynamic-theme-color="true"]';
const listeners = new Set<() => void>();
let cachedSnapshot = {
  appearanceMode: "system" as AppearanceMode,
  resolvedTheme: "light" as "light" | "dark",
};
const serverSnapshot = cachedSnapshot;

export function readAppearanceModePreference(): AppearanceMode {
  if (insideHeyDayNewsApp || typeof window === "undefined") return "system";
  return readSiteAppearance(window.localStorage);
}

function getSnapshot() {
  const appearanceMode = readAppearanceModePreference();
  const systemDark =
    appAppearance() === "dark" ||
    (appAppearance() === null && window.matchMedia(MEDIA_QUERY).matches);
  const resolvedTheme =
    appearanceMode === "system" ? (systemDark ? "dark" : "light") : appearanceMode;
  if (
    cachedSnapshot.appearanceMode !== appearanceMode ||
    cachedSnapshot.resolvedTheme !== resolvedTheme
  ) {
    cachedSnapshot = { appearanceMode, resolvedTheme };
  }
  return cachedSnapshot;
}

function ensureThemeColorMetaTag(): HTMLMetaElement {
  let element = document.querySelector<HTMLMetaElement>(DYNAMIC_THEME_COLOR_SELECTOR);
  if (element) {
    return element;
  }

  element = document.createElement("meta");
  element.name = THEME_COLOR_META_NAME;
  element.setAttribute("data-dynamic-theme-color", "true");
  document.head.append(element);
  return element;
}

function normalizeThemeColor(value: string | null | undefined): string | null {
  const normalizedValue = value?.trim().toLowerCase();
  if (
    !normalizedValue ||
    normalizedValue === "transparent" ||
    normalizedValue === "rgba(0, 0, 0, 0)" ||
    normalizedValue === "rgba(0 0 0 / 0)"
  ) {
    return null;
  }

  return value?.trim() ?? null;
}

function resolveBrowserChromeSurface(): HTMLElement {
  return (
    document.querySelector<HTMLElement>("main[data-slot='sidebar-inset']") ??
    document.querySelector<HTMLElement>("[data-slot='sidebar-inner']") ??
    document.body
  );
}

export function syncBrowserChromeTheme() {
  if (typeof document === "undefined" || typeof getComputedStyle === "undefined") return;
  const rootStyles = getComputedStyle(document.documentElement);
  const themeChromeColor = document.documentElement.dataset.themeId
    ? normalizeThemeColor(rootStyles.getPropertyValue("--app-chrome-background"))
    : null;
  const surfaceColor = normalizeThemeColor(
    getComputedStyle(resolveBrowserChromeSurface()).backgroundColor,
  );
  const fallbackColor = normalizeThemeColor(getComputedStyle(document.body).backgroundColor);
  const backgroundColor = themeChromeColor ?? surfaceColor ?? fallbackColor;
  if (!backgroundColor) return;

  document.documentElement.style.backgroundColor = backgroundColor;
  document.body.style.backgroundColor = backgroundColor;
  // Update every theme-color meta so any element another layer added (for
  // example a media-scoped one) carries the resolved color too.
  const themeColorMetas = document.querySelectorAll<HTMLMetaElement>(
    `meta[name="${THEME_COLOR_META_NAME}"]`,
  );
  if (themeColorMetas.length === 0) {
    ensureThemeColorMetaTag().setAttribute("content", backgroundColor);
  }
  for (const element of themeColorMetas) {
    element.setAttribute("content", backgroundColor);
  }
  reportSurfaceToApp(backgroundColor);
}

function applyAppearance() {
  const { resolvedTheme } = getSnapshot();
  applyThemePalette(insideHeyDayNewsApp ? (appTheme() ?? "heydex") : "heyday", resolvedTheme);
  document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  syncBrowserChromeTheme();
  for (const listener of listeners) listener();
}

let removeWindowListeners: (() => void) | undefined;
function subscribe(listener: () => void) {
  listeners.add(listener);
  if (!removeWindowListeners) {
    const media = window.matchMedia(MEDIA_QUERY);
    const storage = (event: StorageEvent) => {
      if (event.key === SITE_APPEARANCE_KEY || event.key === null) applyAppearance();
    };
    media.addEventListener("change", applyAppearance);
    window.addEventListener("storage", storage);
    removeWindowListeners = () => {
      media.removeEventListener("change", applyAppearance);
      window.removeEventListener("storage", storage);
    };
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      removeWindowListeners?.();
      removeWindowListeners = undefined;
    }
  };
}

if (typeof document !== "undefined" && typeof window !== "undefined") {
  applyAppearance();
  onAppCommand((command) => {
    if (command.type === "appearance") applyAppearance();
  });
}

export function useTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot);
  const setAppearanceMode = useCallback((mode: AppearanceMode): boolean => {
    try {
      writeSiteAppearance(window.localStorage, mode);
      applyAppearance();
      window.dispatchEvent(new StorageEvent("storage", { key: SITE_APPEARANCE_KEY }));
      return true;
    } catch (cause) {
      console.error("Could not save appearance", cause);
      return false;
    }
  }, []);
  return { ...snapshot, setAppearanceMode };
}
